import React, { Fragment, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import { getUploader } from '@/modules/file/uploader'
import DropZoneUploader from '@/modules/base/components/file-uploader/drop-zone-uploader/drop-zone-uploader'
import { Title } from '@/modules/base/components/texts'

import UploadsIcon from '@assets/images/icons/picture_upload.svg'
import DeleteIcon from '@assets/images/icons/trashcan.svg'

import { toast } from '@/modules/base/components/toast/toastConfig'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'

const MAX_SCREENSHOTS = 8
const MAX_FILESIZE_BYTES = 5000000

const useStyles = makeStyles(theme => ({
  uploadIcon: {
    fill: theme.primary,
  },
  screenshots: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    margin: '0 -10px',
  },
  screenshotContainer: {
    flexBasis: 'calc(50% - 20px)',
    margin: 10,
    position: 'relative',
    '&:after': {
      content: '""',
      display: 'block',
      paddingBottom: '100%',
    },
  },
  screenshotContent: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  screenshotInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    position: 'relative',
    '&:hover $removeScreenshot': {
      display: 'flex',
    },
    overflow: 'hidden',
    '& img': {
      objectFit: 'contain',
      width: '100%',
      height: '100%',
    },
  },
  removeScreenshot: {
    width: 25,
    height: 25,
    position: 'absolute',
    right: 10,
    top: 10,
    background: theme.primary,
    display: 'none',
    justifyContent: 'center',
    alignItems: 'center',
    '& path': {
      fill: '#fff',
    },
    borderRadius: '50%',
    cursor: 'pointer',
  },
}))

const Screenshot = ({ src, onDelete, disabled }) => {
  const classes = useStyles()

  return (
    <ScreenshotContainer>
      <div className={classes.screenshotInner}>
        <img src={src} />
        {!disabled && (
          <SVGIcon
            svg={DeleteIcon}
            width="10px"
            height="10px"
            onClick={onDelete}
            className={classes.removeScreenshot}
          />
        )}
      </div>
    </ScreenshotContainer>
  )
}

const ScreenshotContainer = ({ children }) => {
  const classes = useStyles()

  return (
    <div className={classes.screenshotContainer}>
      <div className={classes.screenshotContent}>{children}</div>
    </div>
  )
}

const ScreenshotUpload = ({
  allowedExtensions,
  newScreenshots,
  setNewScreenshots,
  displayScreenshots,
  setDisplayScreenshots,
}) => {
  const classes = useStyles()

  const onError = (id, name, errorReason, xhr) => {
    toast.showToast({ type: 'error', message: errorReason })
  }

  const onSubmitted = (id, name) => {
    const file = uploader.methods.getFile(id)
    const reader = new FileReader()
    reader.onabort = (error) => {
      toast.showToast({ type: 'error', message: error })
    }
    reader.onerror = (error) => {
      toast.showToast({ type: 'error', message: error })
    }
    reader.onload = () => {
      const image = reader.result
      displayScreenshots.push(image)
      setDisplayScreenshots(displayScreenshots)

      newScreenshots.push({ file, image })
      setNewScreenshots(newScreenshots)
    }
    reader.readAsDataURL(file)
  }

  const uploader = getUploader({
    autoUpload: false,
    validation: {
      allowedExtensions,
      sizeLimit: MAX_FILESIZE_BYTES,
    },
    messages: {
      typeError: lang.fileUploaderTypesError.replace('%s', allowedExtensions.join(', ')),
      sizeError: lang.fileUploaderSizeError,
    },
    request: {},
    callbacks: {
      onError,
      onSubmitted,
    },
  })

  const screenshots = lang.marketplace.form.details.sections.screenshots

  return (
    <ScreenshotContainer>
      <DropZoneUploader uploader={uploader}>
        <div className={classes.screenshotInner}>
          <SVGIcon
            width="83.3px"
            svg={UploadsIcon}
            className={classes.uploadIcon}
          />
          <Title size="small">{screenshots.add.claim}</Title>
          <p>{screenshots.add.dependencies(allowedExtensions, MAX_FILESIZE_BYTES)}</p>
        </div>
      </DropZoneUploader>
    </ScreenshotContainer>
  )
}

const Screenshots = ({
  allowedExtensions,
  oldScreenshots,
  setOldScreenshots,
  newScreenshots,
  setNewScreenshots,
  disabled,
}) => {
  const classes = useStyles()
  const [displayScreenshots, setDisplayScreenshots] = useState([])

  useEffect(() => {
    setDisplayScreenshots(oldScreenshots.concat(newScreenshots.map(s => s.image)))
  }, [oldScreenshots, newScreenshots])

  const remove = idx => {
    const image = displayScreenshots[idx]

    const currentNewScreenshots = [...newScreenshots].filter(s => s.image !== image)
    setNewScreenshots(currentNewScreenshots)

    const currentOldScreenshots = [...oldScreenshots]
    currentOldScreenshots.splice(idx, 1)
    setOldScreenshots(currentOldScreenshots)
  }

  const screenshots = lang.marketplace.form.details.sections.screenshots

  return (
    <Fragment>
      <Title size="small" className={classes.sectionHeadline}>{screenshots.headline}</Title>
      <p dangerouslySetInnerHTML={{ __html: screenshots.text(MAX_SCREENSHOTS) }} />
      <div className={classes.screenshots}>
        {!disabled && displayScreenshots.length < MAX_SCREENSHOTS
          && <ScreenshotUpload
            allowedExtensions={allowedExtensions}
            newScreenshots={[...newScreenshots]}
            setNewScreenshots={setNewScreenshots}
            displayScreenshots={[...displayScreenshots]}
            setDisplayScreenshots={setDisplayScreenshots}
          />
        }
        {!disabled && !displayScreenshots.length
          && <ScreenshotUpload
            allowedExtensions={allowedExtensions}
            newScreenshots={[...newScreenshots]}
            setNewScreenshots={setNewScreenshots}
            displayScreenshots={[...displayScreenshots]}
            setDisplayScreenshots={setDisplayScreenshots}
          />
        }
        {displayScreenshots.map((src, idx) => (
          <Screenshot
            key={idx}
            src={src}
            onDelete={() => remove(idx)}
            disabled={disabled}
          />),
        )}
      </div>
    </Fragment>
  )
}

Screenshots.propTypes = {
  allowedExtensions: PropTypes.array.isRequired,
  screenshots: PropTypes.array.isRequired,
  setScreenshots: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

Screenshots.defaultProps = {
  allowedExtensions: ['jpg', 'png'],
  screenshots: [],
  setScreenshots: () => {},
  disabled: false,
}

export default Screenshots
